if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        if (this.classList.contains('quick-add-modal--closing')) return;

        if (this.isCartUpsellQuickAdd && this.hasAttribute('open')) {
          const content = this.querySelector('.quick-add-modal__content');
          this.cartUpsellAnimation?.cancel();

          if (!content || typeof content.animate !== 'function') {
            this.finishHide(preventFocus);
            return;
          }

          this.cartUpsellAnimation = content.animate(
            [
              { transform: 'translate3d(-50%, 0, 0)' },
              { transform: 'translate3d(-50%, calc(100% + 2rem), 0)' },
            ],
            {
              duration: 420,
              easing: 'cubic-bezier(0.4, 0, 0.7, 0.2)',
              fill: 'forwards',
            }
          );

          const animation = this.cartUpsellAnimation;
          const finish = () => {
            if (this.cartUpsellAnimation !== animation) return;

            // `fill: forwards` is useful while the closing frame is visible, but
            // the finished WAAPI effect must not survive into the next cart open.
            // Cancel it before hiding so a later open always starts from clean CSS.
            animation.cancel();
            this.cartUpsellAnimation = null;
            this.finishHide(preventFocus);
          };

          animation.addEventListener('finish', finish, { once: true });
          animation.addEventListener('cancel', () => {
            if (this.cartUpsellAnimation === animation) this.cartUpsellAnimation = null;
          }, { once: true });
          return;
        }

        if (this.classList.contains('quick-add-modal--impact-drawer') && this.hasAttribute('open')) {
          this.classList.add('quick-add-modal--closing');
          const content = this.querySelector('.quick-add-modal__content');
          let finished = false;
          let timeoutId;

          const finish = () => {
            if (finished) return;
            finished = true;
            if (content) content.removeEventListener('transitionend', onTransitionEnd);
            window.clearTimeout(timeoutId);
            this.finishHide(preventFocus);
          };

          const onTransitionEnd = (event) => {
            if (event.target === content && event.propertyName === 'transform') finish();
          };

          if (content) content.addEventListener('transitionend', onTransitionEnd);
          timeoutId = window.setTimeout(finish, 560);
          return;
        }

        this.finishHide(preventFocus);
      }

      finishHide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this.isCartUpsellQuickAdd) {
          const content = this.querySelector('.quick-add-modal__content');
          content?.getAnimations?.().forEach((animation) => animation.cancel());
          this.cartUpsellAnimation = null;
        }

        const hasStaticImpactSheet =
          this.classList.contains('quick-add-modal--impact-drawer') &&
          this.modalContent.querySelector('impact-quick-add-sheet');
        if (!hasStaticImpactSheet) this.modalContent.innerHTML = '';
        this.classList.remove('quick-add-modal--closing', 'halden-cart-upsell-entering', 'halden-cart-upsell-leaving');

        const opener = this.openedBy;
        if (preventFocus) this.openedBy = null;
        super.hide();
        this.unlockBackgroundScroll();

        // When quick-add is launched from the cart drawer, the cart itself must
        // remain scroll-locked and keep its focus trap after the child modal closes.
        if (this.openedFromCartDrawer) {
          const cartDrawer = this.cartDrawerParent || document.querySelector('cart-drawer.active');
          if (cartDrawer) {
            document.body.classList.add('overflow-hidden');
            const focusContainer = cartDrawer.classList.contains('is-empty')
              ? cartDrawer.querySelector('.drawer__inner-empty')
              : document.getElementById('CartDrawer');
            if (focusContainer && opener && document.contains(opener)) {
              requestAnimationFrame(() => trapFocus(focusContainer, opener));
            }
          }
        }
        if (this.cartDrawerParent) {
          this.cartDrawerParent.classList.remove('halden-cart-upsell-child-open');
        }
        this.cartDrawerParent = null;
        this.isCartUpsellQuickAdd = false;
        this.openedFromCartDrawer = false;
      }

      lockBackgroundScroll() {
        if (!this.classList.contains('quick-add-modal--impact-drawer') || this.backgroundScrollLock) return;

        // The cart drawer already locks page scrolling. Fixing <body> a second
        // time changes its containing block on mobile and makes the cart jump.
        if (this.openedFromCartDrawer) return;

        const body = document.body;
        const html = document.documentElement;
        this.backgroundScrollLock = {
          scrollX: window.scrollX,
          scrollY: window.scrollY,
          bodyPosition: body.style.position,
          bodyTop: body.style.top,
          bodyLeft: body.style.left,
          bodyRight: body.style.right,
          bodyWidth: body.style.width,
          htmlOverflow: html.style.overflow,
        };

        // Unlike overflow:hidden alone, fixing the page also prevents iOS
        // touch scrolling behind the open Impact quick-add drawer.
        body.style.position = 'fixed';
        body.style.top = `-${this.backgroundScrollLock.scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        html.style.overflow = 'hidden';
      }

      unlockBackgroundScroll() {
        const lock = this.backgroundScrollLock;
        if (!lock) return;
        this.backgroundScrollLock = null;

        const body = document.body;
        const html = document.documentElement;
        body.style.position = lock.bodyPosition;
        body.style.top = lock.bodyTop;
        body.style.left = lock.bodyLeft;
        body.style.right = lock.bodyRight;
        body.style.width = lock.bodyWidth;
        html.style.overflow = lock.htmlOverflow;
        window.scrollTo(lock.scrollX, lock.scrollY);
      }

      show(opener) {
        this.classList.remove('quick-add-modal--closing');
        this.openedFromCartDrawer = Boolean(opener?.closest('cart-drawer'));
        this.cartDrawerParent = this.openedFromCartDrawer ? opener.closest('cart-drawer') : null;
        this.isCartUpsellQuickAdd = this.classList.contains('halden-cart-upsell-quick-add');

        // Keep the already-open cart drawer completely stationary while its
        // cart-upsell quick-add sheet animates above it. Collection quick-adds
        // do not receive this state and keep their existing behavior.
        if (this.isCartUpsellQuickAdd && this.cartDrawerParent) {
          this.cartDrawerParent.classList.add('halden-cart-upsell-child-open');

          // iOS Safari can keep a fixed-position descendant constrained by the
          // cart/upsell scrolling and compositing context. Portal this cart-only
          // quick-add directly under <body> before opening so its fixed overlay
          // is always relative to the viewport and can cover the whole cart.
          if (this.parentElement !== document.body) {
            document.body.appendChild(this);
          }

          // The cart drawer itself sits at z-index 1000. Once this modal is
          // portaled to <body>, force the cart-only sheet into a higher root
          // stacking layer so iOS cannot paint the drawer above it.
          this.style.zIndex = '10001';
        }

        // Cart-only animation. Use the Web Animations API on this sheet alone so
        // the collection quick-add transition remains untouched and the open cart
        // drawer underneath never participates in the movement.
        const animateCartUpsell = this.isCartUpsellQuickAdd && this.openedFromCartDrawer;

        const startCartUpsellAnimation = () => {
          if (!animateCartUpsell) return;
          const content = this.querySelector('.quick-add-modal__content');
          if (!content || typeof content.animate !== 'function') return;

          // A previous closing animation can otherwise remain in the WAAPI effect
          // stack after the cart drawer itself has been closed and reopened.
          // Clear every animation on this cart-only sheet before starting fresh.
          content.getAnimations?.().forEach((animation) => animation.cancel());
          this.cartUpsellAnimation?.cancel();
          this.cartUpsellAnimation = null;
          this.classList.remove('quick-add-modal--opening', 'quick-add-modal--closing');

          this.cartUpsellAnimation = content.animate(
            [
              { transform: 'translate3d(-50%, calc(100% + 2rem), 0)' },
              { transform: 'translate3d(-50%, 0, 0)' },
            ],
            {
              duration: 480,
              easing: 'cubic-bezier(0.22, 0.7, 0.28, 1)',
              fill: 'none',
            }
          );

          const animation = this.cartUpsellAnimation;
          animation.addEventListener('finish', () => {
            if (this.cartUpsellAnimation === animation) this.cartUpsellAnimation = null;
          }, { once: true });
          animation.addEventListener('cancel', () => {
            if (this.cartUpsellAnimation === animation) this.cartUpsellAnimation = null;
          }, { once: true });
        };

        // Review modals contain the same pre-rendered Impact sheet as collection cards.
        // Look inside the modal itself as well: Shopify may reinsert sections in the editor.
        const staticImpactSheet = this.classList.contains('quick-add-modal--impact-drawer')
          ? this.querySelector('impact-quick-add-sheet')
          : null;

        if (staticImpactSheet) {
          this.modalContent.scrollTop = 0;
          // A gift/variant initialization error must not stop the modal from opening.
          try {
            staticImpactSheet.prepareForOpen?.();
          } catch (error) {
            console.error('HALDEN quick-add initialization:', error);
          }
          super.show(opener);
          this.lockBackgroundScroll();
          startCartUpsellAnimation();
          return;
        }

        // If a theme-editor render has not supplied the static sheet, use the
        // product URL instead of leaving the review button disabled indefinitely.
        const productUrl = opener.getAttribute('data-product-url');
        if (!productUrl) {
          console.error('Quick-add modal has no product URL or pre-rendered sheet');
          return;
        }
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Product quick-add content not found');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
            this.lockBackgroundScroll();
            startCartUpsellAnimation();
          })
          .catch((error) => {
            console.error('Unable to open quick-add modal:', error);
            // Never leave the button in a permanently dimmed/disabled state.
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

if (!customElements.get('impact-quick-add-sheet')) {
  customElements.define(
    'impact-quick-add-sheet',
    class ImpactQuickAddSheet extends HTMLElement {
      constructor() {
        super();
        this.onOptionChange = this.onOptionChange.bind(this);
        this.onThumbnailClick = this.onThumbnailClick.bind(this);
        this.onGalleryScroll = this.onGalleryScroll.bind(this);
      }

      connectedCallback() {
        const variantsElement = this.querySelector('[data-impact-quick-add-variants]');
        if (!variantsElement) return;

        try {
          this.variants = JSON.parse(variantsElement.textContent);
        } catch (error) {
          console.error('Unable to parse quick-add variants', error);
          this.variants = [];
        }

        this.optionInputs = Array.from(this.querySelectorAll('[data-impact-option-input]'));
        this.optionGroups = Array.from(this.querySelectorAll('[data-impact-option-group]'));
        this.variantIdInput = this.querySelector('[data-impact-quick-add-variant-id]');
        this.quantityInput = this.querySelector('[data-impact-quick-add-quantity]');
        this.priceElement = this.querySelector('[data-impact-quick-add-price]');
        this.compareElement = this.querySelector('[data-impact-quick-add-compare]');
        this.submitButton = this.querySelector('.halden-impact-quick-add__submit');
        this.submitLabel = this.querySelector('[data-impact-add-label]');
        this.soldOutMessage = this.querySelector('.sold-out-message');
        this.detailsLink = this.querySelector('[data-impact-quick-add-details]');
        this.selectedVariantElement = this.querySelector('[data-impact-selected-variant]');
        this.galleryTrack = this.querySelector('[data-impact-gallery-track]');
        this.gallerySlides = Array.from(this.querySelectorAll('[data-impact-gallery-slide]'));
        this.thumbnailTrack = this.querySelector('[data-impact-gallery-thumbnails]');
        this.galleryThumbnails = Array.from(this.querySelectorAll('[data-impact-gallery-thumbnail]'));
        this.galleryScrollTimer = null;
        this.activeMediaId = null;
        this.giftSlot = this.querySelector('[data-impact-quick-add-gifts]');
        this.giftState = 'idle';
        this.giftPromise = null;

        this.optionInputs.forEach((input) => input.addEventListener('change', this.onOptionChange));
        this.galleryThumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', this.onThumbnailClick));
        this.galleryTrack?.addEventListener('scroll', this.onGalleryScroll, { passive: true });

        this.applySwatchFallbacks();
        this.updateSelectedLabels();
        this.updateAvailability();
        this.updateFromSelection(true, 'auto');
      }

      disconnectedCallback() {
        this.optionInputs?.forEach((input) => input.removeEventListener('change', this.onOptionChange));
        this.galleryThumbnails?.forEach((thumbnail) => thumbnail.removeEventListener('click', this.onThumbnailClick));
        this.galleryTrack?.removeEventListener('scroll', this.onGalleryScroll);
        window.clearTimeout(this.galleryScrollTimer);
      }

      prepareForOpen() {
        this.updateSelectedLabels();
        this.updateAvailability();
        this.updateFromSelection(true, 'auto');
        // Keep checkout disabled until the product page's actual gift block is
        // available. Reopening after an error retries the request.
        this.ensureGiftBlock();
      }

      ensureGiftBlock() {
        if (!this.giftSlot || this.giftState === 'ready') return Promise.resolve();
        if (this.giftPromise) return this.giftPromise;

        this.giftState = 'loading';
        this.giftSlot.hidden = false;
        this.giftSlot.textContent = 'Loading complimentary gift…';
        this.updateSubmitButton(this.findVariant(this.selectedOptions));

        this.giftPromise = (async () => {
          const response = await fetch(this.dataset.giftProductUrl, {
            headers: { Accept: 'text/html' },
          });
          if (!response.ok) throw new Error('Unable to load the product gift');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = [...html.querySelectorAll('[data-halden-gift-products]')]
            .find((element) => element.dataset.parentProductId === this.dataset.giftProductId);

          this.giftSlot.replaceChildren();
          if (source) {
            // The product page is the source of truth: same gifts, copy and
            // editor colors as the existing HALDEN Gifts block.
            const cloned = source.cloneNode(true);
            cloned.removeAttribute('id');
            cloned.removeAttribute('data-shopify-editor-block');
            this.giftSlot.append(cloned);
            this.giftSlot.hidden = false;
            window.HaldenProductGifts?.applyState?.(this.giftSlot);

            const stylesheetUrl = this.dataset.giftStylesheetUrl;
            if (stylesheetUrl && ![...document.querySelectorAll('link[rel="stylesheet"]')]
              .some((link) => link.href === new URL(stylesheetUrl, location.href).href)) {
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = stylesheetUrl;
              document.head.append(link);
            }
          } else {
            this.giftSlot.hidden = true;
          }
          this.giftState = 'ready';
        })().catch((error) => {
          console.error('HALDEN quick-add gift:', error);
          this.giftState = 'error';
          this.giftSlot.textContent = 'Could not load the complimentary gift. Close and reopen to retry.';
          this.giftSlot.hidden = false;
        }).finally(() => {
          this.giftPromise = null;
          this.updateSubmitButton(this.findVariant(this.selectedOptions));
        });
        return this.giftPromise;
      }

      get selectedOptions() {
        return this.optionGroups.map((group) => group.querySelector('input:checked')?.value || '');
      }

      onOptionChange(event) {
        const changedInput = event.target.closest('[data-impact-option-input]');
        if (!changedInput) return;

        let variant = this.findVariant(this.selectedOptions);
        if (!variant?.available) {
          const changedIndex = Number(changedInput.dataset.optionIndex);
          variant = this.variants.find(
            (candidate) => candidate.available && candidate.options?.[changedIndex] === changedInput.value
          );
          if (variant) this.selectVariantOptions(variant);
        }

        this.updateSelectedLabels();
        this.updateAvailability();
        this.updateFromSelection(true, 'smooth');
      }

      onThumbnailClick(event) {
        const thumbnail = event.currentTarget;
        if (!thumbnail?.dataset.mediaId) return;
        this.selectMedia(thumbnail.dataset.mediaId, true);
      }

      onGalleryScroll() {
        window.clearTimeout(this.galleryScrollTimer);
        this.galleryScrollTimer = window.setTimeout(() => this.syncVariantFromGallery(), 120);
      }

      syncVariantFromGallery() {
        if (!this.galleryTrack || this.gallerySlides.length < 2) return;

        const trackRect = this.galleryTrack.getBoundingClientRect();
        let nearestSlide = null;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.gallerySlides.forEach((slide) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestSlide = slide;
          }
        });

        const mediaId = nearestSlide?.dataset.mediaId;
        if (mediaId && mediaId !== this.activeMediaId) this.selectMedia(mediaId, false);
      }

      selectMedia(mediaId, shouldScroll) {
        const variant = this.findBestVariantForMedia(mediaId);
        if (variant) {
          this.selectVariantOptions(variant);
          this.updateSelectedLabels();
          this.updateAvailability();
          this.updateFromSelection(false);
        }

        this.setActiveMedia(mediaId, shouldScroll ? 'smooth' : null);
      }

      findBestVariantForMedia(mediaId) {
        const candidates = this.variants.filter(
          (variant) => variant.media_id != null && String(variant.media_id) === String(mediaId)
        );
        if (!candidates.length) return null;

        const availableCandidates = candidates.filter((variant) => variant.available);
        const pool = availableCandidates.length ? availableCandidates : candidates;
        const selectedOptions = this.selectedOptions;

        return pool.reduce((best, candidate) => {
          const candidateScore = candidate.options?.reduce(
            (score, value, index) => score + (value === selectedOptions[index] ? 1 : 0),
            0
          ) || 0;
          const bestScore = best.options?.reduce(
            (score, value, index) => score + (value === selectedOptions[index] ? 1 : 0),
            0
          ) || 0;
          return candidateScore > bestScore ? candidate : best;
        }, pool[0]);
      }

      findVariant(options) {
        if (!this.optionGroups.length) {
          const currentVariantId = String(this.variantIdInput?.value || '');
          return (
            this.variants.find((variant) => String(variant.id) === currentVariantId) ||
            this.variants.find((variant) => variant.available) ||
            this.variants[0] ||
            null
          );
        }

        return this.variants.find(
          (variant) =>
            Array.isArray(variant.options) &&
            variant.options.length === options.length &&
            variant.options.every((value, index) => value === options[index])
        );
      }

      selectVariantOptions(variant) {
        if (!variant?.options) return;
        this.optionGroups.forEach((group, index) => {
          const matchingInput = Array.from(group.querySelectorAll('[data-impact-option-input]')).find(
            (input) => input.value === variant.options[index]
          );
          if (matchingInput) matchingInput.checked = true;
        });
      }

      updateFromSelection(syncGallery = true, galleryBehavior = 'smooth') {
        const variant = this.findVariant(this.selectedOptions);
        this.updateVariantInput(variant);
        this.updateDetailsLink(variant);
        this.updatePrice(variant);
        this.updateSelectedVariantName(variant);
        this.updateSubmitButton(variant);
        if (syncGallery) this.updateGallery(variant, galleryBehavior);
      }

      updateVariantInput(variant) {
        if (!this.variantIdInput) return;
        if (variant) {
          this.variantIdInput.value = variant.id;
          this.variantIdInput.disabled = !variant.available;
        } else {
          this.variantIdInput.value = '';
          this.variantIdInput.disabled = true;
        }

        if (this.quantityInput) this.quantityInput.value = variant?.quantity_min || 1;
      }

      updateDetailsLink(variant) {
        if (!this.detailsLink) return;
        const productUrl = this.detailsLink.dataset.productUrl || this.detailsLink.getAttribute('href') || '';
        if (!productUrl) return;
        this.detailsLink.href = variant?.id ? `${productUrl}?variant=${variant.id}` : productUrl;
      }

      updatePrice(variant) {
        if (!variant) return;
        if (this.priceElement) this.priceElement.textContent = variant.price || '';

        if (!this.compareElement) return;
        if (variant.compare_at_price) {
          this.compareElement.textContent = variant.compare_at_price;
          this.compareElement.classList.remove('hidden');
        } else {
          this.compareElement.textContent = '';
          this.compareElement.classList.add('hidden');
        }
      }

      updateSelectedVariantName(variant) {
        if (!this.selectedVariantElement) return;
        this.selectedVariantElement.textContent = variant?.title || '';
      }

      updateGallery(variant, behavior = 'smooth') {
        if (!this.gallerySlides.length) return;
        const mediaId = variant?.media_id != null ? String(variant.media_id) : null;
        const hasMedia = mediaId && this.gallerySlides.some((slide) => slide.dataset.mediaId === mediaId);
        this.setActiveMedia(hasMedia ? mediaId : this.activeMediaId, behavior);
      }

      setActiveMedia(mediaId, behavior = 'smooth') {
        if (!this.gallerySlides.length) return;

        let activeSlide = mediaId
          ? this.gallerySlides.find((slide) => slide.dataset.mediaId === String(mediaId))
          : null;
        if (!activeSlide) activeSlide = this.gallerySlides.find((slide) => slide.classList.contains('is-active'));
        if (!activeSlide) activeSlide = this.gallerySlides[0];

        const activeId = activeSlide.dataset.mediaId || null;
        this.activeMediaId = activeId;
        this.updateGalleryAspectRatio(activeSlide);

        this.gallerySlides.forEach((slide) => {
          slide.classList.toggle('is-active', slide === activeSlide);
        });

        let activeThumbnail = null;
        this.galleryThumbnails.forEach((thumbnail) => {
          const isActive = activeId != null && thumbnail.dataset.mediaId === activeId;
          thumbnail.classList.toggle('is-active', isActive);
          thumbnail.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          if (isActive) activeThumbnail = thumbnail;
        });

        if (behavior && this.galleryTrack && this.gallerySlides.length > 1) {
          this.galleryTrack.scrollTo({ left: activeSlide.offsetLeft, behavior });
        }

        if (activeThumbnail && this.thumbnailTrack) {
          const targetLeft = activeThumbnail.offsetLeft - (this.thumbnailTrack.clientWidth - activeThumbnail.offsetWidth) / 2;
          this.thumbnailTrack.scrollTo({ left: Math.max(0, targetLeft), behavior: behavior || 'auto' });
        }
      }

      updateGalleryAspectRatio(activeSlide) {
        if (!this.galleryTrack || !activeSlide) return;

        const width = Number(activeSlide.dataset.mediaWidth);
        const height = Number(activeSlide.dataset.mediaHeight);
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;

        this.galleryTrack.style.setProperty('--halden-impact-active-media-ratio', `${width} / ${height}`);
      }

      updateSubmitButton(variant) {
        if (!this.submitButton || !this.submitLabel) return;
        const available = Boolean(variant?.available);
        const label = variant
          ? available
            ? this.dataset.addToCartText
            : this.dataset.soldOutText
          : this.dataset.unavailableText;

        this.submitLabel.textContent = label || '';
        this.submitLabel.classList.remove('hidden');
        this.soldOutMessage?.classList.add('hidden');
        const ready = this.giftState === 'ready';
        this.submitButton.disabled = !available || !ready;
        if (available && ready) {
          this.submitButton.removeAttribute('aria-disabled');
        } else {
          this.submitButton.setAttribute('aria-disabled', 'true');
        }
      }

      updateSelectedLabels() {
        this.optionGroups.forEach((group) => {
          const selected = group.querySelector('[data-impact-option-input]:checked');
          const output = group.querySelector('[data-impact-selected-value]');
          if (selected && output) output.textContent = selected.value;
        });
      }

      updateAvailability() {
        if (!this.variants.length || !this.optionGroups.length) return;
        this.optionGroups.forEach((group, groupIndex) => {
          group.querySelectorAll('[data-impact-option-input]').forEach((input) => {
            const possible = this.variants.some(
              (variant) => variant.available && variant.options?.[groupIndex] === input.value
            );
            const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            input.disabled = !possible;
            label?.classList.toggle('is-unavailable', !possible);
          });
        });
      }

      applySwatchFallbacks() {
        const colorAliases = {
          black: '#1f1f1f',
          preto: '#1f1f1f',
          white: '#f4f2ec',
          branco: '#f4f2ec',
          ivory: '#f2eadb',
          cream: '#eee2c9',
          creme: '#eee2c9',
          beige: '#d6c5a8',
          tan: '#b9936c',
          khaki: '#a79a78',
          brown: '#82522f',
          marrom: '#82522f',
          camel: '#b47b45',
          grey: '#a9aaad',
          gray: '#a9aaad',
          cinza: '#a9aaad',
          charcoal: '#45494c',
          navy: '#1d2d4f',
          blue: '#315b91',
          azul: '#315b91',
          green: '#2d7743',
          verde: '#2d7743',
          olive: '#737343',
          red: '#a82b2b',
          vermelho: '#a82b2b',
          burgundy: '#6f2636',
          wine: '#6f2636',
          orange: '#d17a2c',
          laranja: '#d17a2c',
          yellow: '#e0bd3d',
          amarelo: '#e0bd3d',
          purple: '#694c82',
          roxo: '#694c82',
          pink: '#d9899d',
          rosa: '#d9899d',
          gold: '#b8974f',
          dourado: '#b8974f',
          silver: '#b7b7b7',
          prata: '#b7b7b7',
        };

        this.querySelectorAll('[data-impact-swatch]').forEach((swatch) => {
          if (swatch.style.getPropertyValue('--halden-impact-swatch')) return;
          const raw = (swatch.dataset.swatchFallback || '').trim();
          const normalized = raw
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();

          let fallback = '';
          if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) {
            fallback = raw;
          } else if (window.CSS?.supports?.('color', raw)) {
            fallback = raw;
          } else {
            const alias = Object.keys(colorAliases).find((name) => normalized.includes(name));
            if (alias) fallback = colorAliases[alias];
          }

          swatch.style.setProperty('--halden-impact-swatch', fallback || '#d8d8d8');
        });
      }
    }
  );
}
